import React, { useMemo } from "react";
import { useLayers } from "../../context/LayersContext";
import { mockLayers } from "../../global/constants/layers";
import {
  getLayerStyle,
  hasStyleVariations,
  getStyleVariations,
} from "../../global/layerStyles";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { useCategoricalLegend } from "../../hooks/useCategoricalLegend";
import {
  APL_LAYER_NAME,
  VOCACAO_LAYER_NAME,
} from "../../global/constants/categoricalLegends";
import { useWmsClassLegends } from "../../hooks/useWmsClassLegend";
import { ManualLegend } from "../ManualLegend";
import { WmsClassLegend } from "../WmsClassLegend";
import { CONJ_DGC_LAYER_NAME } from "../../global/constants/dgcConjuntos";
import { DgcInterpretationTable } from "./DgcInterpretationTable";

export function LegendsList() {
  const { layers } = useLayers();

  // Collect titles that are sub-layers of another layer — these are rendered
  // inline under their parent's legend entry and must not appear standalone.
  const subLayerTitles = useMemo(
    () =>
      new Set(
        mockLayers.flatMap((l) => l.subLayers?.map((s) => s.layerTitle) ?? [])
      ),
    []
  );

  // Get active layers (selected or permanent)
  const activeLayers = useMemo(() => {
    return mockLayers
      .filter(
        (layer) =>
          layer.workspace &&
          !subLayerTitles.has(layer.title) &&
          (layer.permanent ||
            (layers && layer.title && layers.includes(layer.title)))
      )
      .sort((a, b) => {
        const zIndexA = a.zIndex ?? 500;
        const zIndexB = b.zIndex ?? 500;
        return zIndexB - zIndexA; // Reverse order for legend (highest z-index first)
      });
  }, [layers, subLayerTitles]);

  // Group layers by category
  const categorizedLayers = useMemo(() => {
    const grouped = activeLayers.reduce((acc, layer) => {
      if (!acc[layer.category]) {
        acc[layer.category] = [];
      }
      acc[layer.category].push(layer);
      return acc;
    }, {} as Record<string, typeof activeLayers>);

    return Object.entries(grouped).map(([category, layers]) => ({
      category,
      layers,
    }));
  }, [activeLayers]);

  const isAplActive = activeLayers.some((l) => l.layerName === APL_LAYER_NAME);
  const aplLegendItems = useCategoricalLegend(APL_LAYER_NAME, isAplActive);

  const isVocacaoActive = activeLayers.some(
    (l) => l.layerName === VOCACAO_LAYER_NAME
  );
  const vocacaoLegendItems = useCategoricalLegend(
    VOCACAO_LAYER_NAME,
    isVocacaoActive
  );

  // Camadas WMS estilizadas no servidor não têm estilo no front: as classes
  // vêm do SLD via GetLegendGraphic.
  const wmsClassLegends = useWmsClassLegends(activeLayers);

  if (categorizedLayers.length === 0) {
    return (
      <div className="p-8 text-center text-[#666]">
        <p>Não há legendas para exibir.</p>
        <p className="mt-2">
          Primeiro selecione alguma camada que deseja visualizar.
        </p>
      </div>
    );
  }

  const currentCategoryLayers = (categoryIndex: number) =>
    categorizedLayers[categoryIndex]?.layers || [];

  // Helper function to render the appropriate preview based on layer style
  const renderPreview = (
    layer: (typeof activeLayers)[0],
    featureProperties?: Record<string, any>
  ) => {
    const customIcon = layer.style?.customIcon;

    if (customIcon) {
      return (
        <img
          src={customIcon}
          alt={layer.title}
          className="w-6 h-6 object-contain flex-shrink-0"
        />
      );
    }

    const styleFunction = getLayerStyle(layer.layerName);
    const mockFeature = featureProperties
      ? { properties: featureProperties }
      : {};
    const style = styleFunction ? styleFunction(mockFeature) : null;

    if (!style) {
      return (
        <div
          className="min-w-[30px] w-[30px] h-0 my-2.5 flex-shrink-0 border-t border-solid border-black"
          style={{ borderTopWidth: "1px" }}
        />
      );
    }

    const dashArray = Array.isArray(style.dashArray)
      ? style.dashArray.join(" ")
      : style.dashArray;

    const isLine =
      style.fillColor === "transparent" ||
      style.fillOpacity === 0 ||
      !style.fillColor;

    if (isLine) {
      return (
        <div
          className="min-w-[30px] w-[30px] h-0 my-2.5 flex-shrink-0"
          style={{
            borderTop: `${style.weight || 1}px ${
              dashArray ? "dashed" : "solid"
            } ${style.color || "#000"}`,
          }}
        />
      );
    }

    return (
      <div
        className="min-w-[30px] w-[30px] h-5 rounded-sm flex-shrink-0"
        style={{
          border: `${style.weight || 1}px ${dashArray ? "dashed" : "solid"} ${
            style.color || "#000"
          }`,
          backgroundColor: style.fillColor || "transparent",
          opacity: Math.max(style.fillOpacity || 0, 0.4),
        }}
      />
    );
  };

  // Helper function to render legend items for a layer
  const renderLayerLegend = (layer: (typeof activeLayers)[0]) => {
    if (layer.manualLegend) {
      return (
        <ManualLegend
          key={`${layer.layerName}-manual-legend`}
          label={layer.manualLegend.label}
          items={layer.manualLegend.legendItems}
        />
      );
    }

    // Camada WMS pintada pelo servidor: as classes vêm do SLD.
    const wmsItems = wmsClassLegends[layer.layerName];
    if (layer.type === "wms" && wmsItems && wmsItems.length > 0) {
      return (
        <WmsClassLegend
          key={`${layer.layerName}-wms-legend`}
          items={wmsItems}
        />
      );
    }

    // Camadas pintadas por hash: uma entrada por classe encontrada na camada
    // (APL, vocação principal), com a cor que o estilo dá àquele valor.
    const categoricalItems =
      layer.layerName === APL_LAYER_NAME
        ? aplLegendItems
        : layer.layerName === VOCACAO_LAYER_NAME
        ? vocacaoLegendItems
        : null;

    if (categoricalItems) {
      return categoricalItems.map((item) => (
        <div
          key={item.label}
          className="flex items-center mb-3 last:mb-0 gap-3"
        >
          <div
            className="min-w-[30px] w-[30px] h-5 rounded-sm flex-shrink-0"
            style={{
              border: "2px solid #000000",
              backgroundColor: item.color,
              opacity: 0.8,
            }}
          />
          <span className="text-[13px] text-gray-light leading-tight">
            {item.label}
          </span>
        </div>
      ));
    }

    // Check if this layer has style variations
    if (hasStyleVariations(layer.layerName)) {
      const variations = getStyleVariations(layer.layerName);
      if (variations) {
        const items = variations.map((variation, index) => (
          <div
            key={`${layer.layerName}-${index}`}
            className="flex items-center mb-3 last:mb-0 gap-3"
          >
            {renderPreview(layer, variation.properties)}
            <span className="text-[13px] text-gray-light leading-tight">
              {variation.label}
            </span>
          </div>
        ));

        if (layer.layerName === CONJ_DGC_LAYER_NAME) {
          items.push(
            <DgcInterpretationTable key={`${layer.layerName}-dgc-table`} />
          );
        }

        return items;
      }
    }

    // Single style layer (no variations)
    return (
      <div
        key={layer.layerName}
        className="flex items-center mb-3 last:mb-0 gap-3"
      >
        {renderPreview(layer)}
        <span className="text-[13px] text-gray-light leading-tight">
          {layer.title}
        </span>
      </div>
    );
  };

  const firstCategory = categorizedLayers[0]?.category || "";

  return (
    <div id="legends-panel" className="w-full h-full flex flex-col bg-white">
      <Tabs
        defaultValue={firstCategory}
        className="w-full flex flex-col h-full"
      >
        <TabsList className="mb-0 bg-white border-b border-[#e0e0e0] rounded-none h-auto p-0 justify-start overflow-x-auto flex-shrink-0">
          {categorizedLayers.map(({ category }) => (
            <TabsTrigger
              key={category}
              value={category}
              className="font-semibold text-[10px] min-h-0 normal-case data-[state=active]:text-green-light data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-green-light data-[state=active]:shadow-none rounded-none px-3 py-2"
            >
              {category}
            </TabsTrigger>
          ))}
        </TabsList>

        {categorizedLayers.map(({ category }, index) => (
          <TabsContent
            key={category}
            value={category}
            className="overflow-y-auto flex-grow p-4 mt-0"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            {currentCategoryLayers(index).map((layer) =>
              renderLayerLegend(layer)
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
